import { useCallback, useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { leerRuta, primeraSeccion, puedeVer, rutaDe } from "../../features/auth/secciones";
import Dashboard from "../../features/dashboard/Dashboard";
import Miembros from "../../features/miembros/Miembros";
import Asistencia from "../../features/asistencia/Asistencia";
import Cursos from "../../features/cursos/Cursos";
import Ninos from "../../features/ninos/Ninos";
import Servidores from "../../features/servidores/Servidores";
import Notificaciones from "../../features/notificaciones/Notificaciones";
import Encuestas from "../../features/encuestas/Encuestas";
import Proyectos from "../../features/proyectos/Proyectos";
import Configuracion from "../../features/configuracion/Configuracion";
import Familias from "../../features/familias/Familias";
import AsistenciaNinos from "../../features/asistencia/AsistenciaNinos";
import Bajas from "../../features/bajas/Bajas";
import VisitasNuevas from "../../features/visitas/VisitasNuevas";
import Reuniones from "../../features/reuniones/Reuniones";
import Sidebar from "./Sidebar";
import ProgramacionServicio from "../../features/programacion/ProgramacionServicio";
import CalificacionesNinos from "../../features/calificaciones/CalificacionesNinos";
import Permanencia from "../../features/miembros/Permanencia";
import ProgramacionKids from "../../features/ninos/ProgramacionKids";
import MisTurnos from "../../features/servidores/MisTurnos";
import LimiteDeError from "../../shared/components/LimiteDeError";
import Marca from "../../shared/components/Marca";
import { menuDe } from "../../features/auth/secciones";

// Panel con menú lateral. Lo usan todos los roles salvo quienes solo ven sus
// turnos; cada uno ve únicamente las secciones que su rol tiene permitidas.
function PastorDashboard({ usuario, onLogout }) {
  // La sección sale de la dirección (p. ej. /calendario); si no puede verla, su primera sección.
  const vistaDeLaDireccion = useCallback(() => {
    const { seccion: deLaRuta, contexto: delaRuta } = leerRuta(window.location.pathname, window.location.search);
    return puedeVer(usuario, deLaRuta) ? { seccion: deLaRuta, contexto: delaRuta } : { seccion: primeraSeccion(usuario), contexto: {} };
  }, [usuario]);
  const [vistaInicial] = useState(vistaDeLaDireccion);
  const [seccion, setSeccion] = useState(vistaInicial.seccion);
  // Contexto de navegación: p. ej. qué reunión abrir al llegar desde Calendario.
  const [contexto, setContexto] = useState(vistaInicial.contexto);
  // Menú desplegable en celular y tableta.
  const [menuAbierto, setMenuAbierto] = useState(false);

  useEffect(() => {
    if (!menuAbierto) return undefined;
    const alPresionar = (e) => e.key === "Escape" && setMenuAbierto(false);
    window.addEventListener("keydown", alPresionar);
    return () => window.removeEventListener("keydown", alPresionar);
  }, [menuAbierto]);

  const irA = (destino, datos = {}) => {
    setContexto(datos);
    setSeccion(destino);
    const ruta = rutaDe(destino, datos);
    if (`${window.location.pathname}${window.location.search}` !== ruta) window.history.pushState(null, "", ruta);
    window.scrollTo(0, 0);
  };

  // Al entrar, la dirección queda con la sección que se abrió (p. ej. de "/" a "/inicio").
  useEffect(() => {
    window.history.replaceState(null, "", rutaDe(vistaInicial.seccion, vistaInicial.contexto));
  }, [vistaInicial]);

  // Botones atrás y adelante del navegador.
  useEffect(() => {
    const alNavegar = () => {
      const vista = vistaDeLaDireccion();
      setSeccion(vista.seccion);
      setContexto(vista.contexto);
    };
    window.addEventListener("popstate", alNavegar);
    return () => window.removeEventListener("popstate", alNavegar);
  }, [vistaDeLaDireccion]);

  // La clave cambia con el contexto para que la pantalla se abra con la reunión elegida.
  const clave = `${seccion}-${contexto.servicioId || ""}`;
  const ver = (id) => seccion === id && puedeVer(usuario, id);
  const tituloSeccion = menuDe(usuario).flatMap((g) => g.opciones).find((o) => o.id === seccion)?.label;
  // Grupo del menú de la sección (p. ej. "Personas" sobre Miembros), para ubicarse.
  const grupoSeccion = menuDe(usuario).find((g) => g.opciones.some((o) => o.id === seccion))?.grupo;

  // El nombre de la pestaña del navegador dice en qué sección se está.
  useEffect(() => {
    document.title = tituloSeccion ? `${tituloSeccion} · Mi Casa` : "Mi Casa · Sistema de gestión";
  }, [tituloSeccion]);

  return (
    <div className="min-h-screen text-white flex">
      <Sidebar
        seccionActiva={seccion}
        setSeccion={(destino) => irA(destino)}
        onLogout={onLogout}
        usuario={usuario}
        abierto={menuAbierto}
        onCerrar={() => setMenuAbierto(false)}
      />

      <div className="min-w-0 flex-1 lg:ml-64">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-white/10 bg-black/70 px-4 py-3 backdrop-blur-md lg:hidden">
        <Marca soloMonograma />
        <button
          onClick={() => setMenuAbierto(true)}
          aria-label="Abrir menú"
          aria-expanded={menuAbierto}
          className="btn-fantasma h-11 w-11 !p-0"
        >
          <Menu size={20} aria-hidden="true" />
        </button>
      </header>

      <main className="pantalla mx-auto w-full max-w-[1440px] sm:p-2 lg:p-4" key={clave}>
        {grupoSeccion && <p className="antetitulo -mb-4 px-6 pt-6">{grupoSeccion}</p>}
        <LimiteDeError>
        {!seccion && <p className="p-6 text-gray-400">Tu rol todavía no tiene secciones asignadas.</p>}
        {ver("dashboard") && <Dashboard irA={irA} usuario={usuario} />}
        {ver("mis_turnos") && (
          <div className="p-6">
            <MisTurnos usuario={usuario} />
          </div>
        )}
        {ver("reuniones") && <Reuniones irA={irA} />}
        {ver("programacion") && <ProgramacionServicio contexto={contexto} />}
        {ver("asistencia") && <Asistencia contexto={contexto} irA={irA} />}
        {ver("servidores") && <Servidores usuario={usuario} contexto={contexto} />}
        {ver("miembros") && <Miembros usuario={usuario} />}
        {ver("familias") && <Familias usuario={usuario} />}
        {ver("visitas_nuevas") && <VisitasNuevas usuario={usuario} />}
        {ver("permanencia") && <Permanencia />}
        {ver("bajas") && <Bajas usuario={usuario} />}
        {ver("kids_programacion") && <ProgramacionKids />}
        {ver("ninos") && <Ninos usuario={usuario} />}
        {ver("asistencia_ninos") && <AsistenciaNinos usuario={usuario} />}
        {ver("calificaciones") && <CalificacionesNinos />}
        {ver("cursos") && <Cursos usuario={usuario} />}
        {ver("notificaciones") && <Notificaciones />}
        {ver("encuestas") && <Encuestas usuario={usuario} />}
        {ver("proyectos") && <Proyectos usuario={usuario} contexto={contexto} irA={irA} />}
        {ver("configuracion") && <Configuracion usuario={usuario} />}
        </LimiteDeError>
      </main>
      </div>
    </div>
  );
}

export default PastorDashboard;
